import React, { useState, useEffect } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';
import { DEPARTMENTS } from '../../utils/constants';
import { 
  validateEmail, 
  validatePhone, 
  validateName, 
  validateExperience,
  validateRequired 
} from '../../utils/validation';

const CandidateForm = ({ candidate, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    experience: 0,
    department: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (candidate) {
      setFormData(candidate);
    }
  }, [candidate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'experience' ? parseInt(value) || 0 : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!validateName(formData.name)) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (min 10 digits)';
    }

    if (!validateRequired(formData.skills)) {
      newErrors.skills = 'Skills are required';
    }

    if (!validateExperience(formData.experience)) {
      newErrors.experience = 'Experience must be between 0 and 50 years';
    }

    if (!validateRequired(formData.department)) {
      newErrors.department = 'Department is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter full name"
          error={errors.name}
          required
        />

        <Input
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter email address"
          error={errors.email}
          required
        />

        <Input
          label="Phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+1234567890"
          error={errors.phone}
          required
        />

        <div className="mb-4">
          <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
            Experience (Years) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="experience"
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            min="0"
            max="50"
            className={`input-field ${errors.experience ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
          {errors.experience && (
            <p className="mt-1 text-sm text-red-600">{errors.experience}</p>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
          Department <span className="text-red-500">*</span>
        </label>
        <select
          id="department"
          name="department"
          value={formData.department}
          onChange={handleChange}
          className={`input-field ${errors.department ? 'border-red-500 focus:ring-red-500' : ''}`}
        >
          <option value="">Select Department</option>
          {DEPARTMENTS.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
        {errors.department && (
          <p className="mt-1 text-sm text-red-600">{errors.department}</p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-1">
          Skills <span className="text-red-500">*</span>
          <span className="text-xs text-gray-500 ml-2">(Comma separated)</span>
        </label>
        <textarea
          id="skills"
          name="skills"
          value={formData.skills}
          onChange={handleChange}
          placeholder="e.g., JavaScript, React, Node.js, MongoDB"
          rows="3"
          className={`input-field ${errors.skills ? 'border-red-500 focus:ring-red-500' : ''}`}
        />
        {errors.skills && (
          <p className="mt-1 text-sm text-red-600">{errors.skills}</p>
        )}
      </div>

      <div className="flex space-x-3 mt-6">
        <Button type="submit" variant="primary" fullWidth disabled={loading}>
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {candidate ? 'Updating...' : 'Creating...'}
            </span>
          ) : (
            candidate ? 'Update Candidate' : 'Create Candidate'
          )}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} fullWidth disabled={loading}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default CandidateForm;